import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Layout from '../../layout/Layout';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import supplierBulkUploadIcon from '../../../assets/supplierBulkUploadIcon.png';
import invoicesNotesIcon from '../../../assets/invoicesNotesIcon.png';
import { generatePriceTemplate } from '../../../utils/excelTemplateGenerator';
import { useAuth } from '../../../context/AuthContext';
import supplierService, { Supplier as ApiSupplier, PriceItem } from '../../../services/supplierService';
import { BackButtonComponent } from '../../ui/DesignSystem';
import { BackLinkComponent } from '../../ui/DesignSystem';
import { API_URL, ENDPOINTS, AUTH_TOKEN_KEY } from '../../../config';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../common/Toast';
import SkuImage from '../../common/SkuImage';
import Pagination from '../../common/Pagination';
import SharedLoadingSpinner from '../../ui/LoadingSpinner';


// Create a wrapper for form and buttons for proper layout
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const PageContainer = styled.div`
  padding: 0.5rem 1.5rem;
  max-width: 100vw;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 60px);
  overflow-x: hidden;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
  }
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 0.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 0;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0; /* Allow shrinking */
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const PageTitle = styled.h1`
  color: #042B41;
  font-size: clamp(20px, 4vw, 28px);
  font-weight: 700;
  margin: 0;
`;

const BulkUploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #042B41;
  color: white;
  border: none;
  padding: 0.625rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  min-width: 120px;
  height: 44px;
  
  &:hover {
    background-color: #031F30;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  img {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    height: 40px;
    min-width: 100px;
  }
`;

const PriceListButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #042B41;
  color: white;
  border: none;
  padding: 0.625rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  min-width: 120px;
  height: 44px;
  
  &:hover {
    background-color: #031F30;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  img {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    height: 40px;
    min-width: 100px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-left: auto;
  
  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    flex-direction: column;
  }
`;

const BreadcrumbNav = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #6B7280;
  flex-wrap: wrap;
  min-width: 0;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 0.5rem;
  color: #6B7280;
  flex-shrink: 0;
`;

const SuppliersText = styled.span`
  color: #6B7280;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SupplierName = styled.span`
  font-weight: 600;
  color: #111827;
  word-break: break-word;
  min-width: 0;
`;

const FormContainer = styled.div`
  width: 100%;
  margin-bottom: 0.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FormLabel = styled.label<{ required?: boolean }>`
  font-size: 0.8rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 0.25rem;
  
  &::after {
    content: ${props => props.required ? '"*"' : '""'};
    color: #EF4444;
    margin-left: 2px;
  }
`;

const FormInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 100%;
  min-width: 0;
  
  &:focus {
    outline: none;
    border-color: #042B41;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }
`;

const TableContainer = styled.div`
  width: 100%;
  max-width: 100vw;
  margin-top: 0.5rem;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  background-color: white;
  overflow: hidden;
  position: relative;
`;

const TableScrollWrapper = styled.div`
  overflow-x: auto;
  overflow-y: visible; /* Changed to visible so table expands to fit content */
  width: 100%;
  max-width: calc(100vw - 2rem);
  height: auto; /* Auto height to fit content exactly */
  
  /* Custom dark scrollbar styling for both horizontal and vertical scrollbars */
  &::-webkit-scrollbar {
    width: 16px;
    height: 16px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f3f4;
    border-radius: 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #4a5568;
    border-radius: 8px;
    border: 2px solid #f1f3f4;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #2d3748;
  }
  
  &::-webkit-scrollbar-corner {
    background: #f1f3f4;
  }
  
  /* Firefox scrollbar */
  scrollbar-width: auto;
  scrollbar-color: #4a5568 #f1f3f4;
`;

const Table = styled.table.withConfig({
  shouldForwardProp: (prop) => prop !== 'isStaffUser',
}) <{ isStaffUser?: boolean }>`
  width: ${props => props.isStaffUser ? '3145px' : '4045px'};
  min-width: ${props => props.isStaffUser ? '3145px' : '4045px'};
  border-collapse: collapse;
  border: none;
  table-layout: fixed;
  background-color: white;
  display: table;
`;

const TableHeader = styled.thead`
  background-color: #042B41;
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
  
  th {
    padding: 12px 8px;
    text-align: center;
    font-weight: 700;
    font-size: 11px;
    border-right: 1px solid rgba(255, 255, 255, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    white-space: nowrap;
    vertical-align: middle;
    line-height: 1.2;
    
    &:last-child:not(.notes-header-column) {
      border-right: none;
    }
    
    /* First row headers */
    tr:first-child & {
      border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    }
    
    /* Second row headers (sub-headers) */
    tr:last-child & {
      border-top: 1px solid rgba(255, 255, 255, 0.3);
      border-bottom: 2px solid rgba(255, 255, 255, 0.4);
    }
    
    /* Optimized column widths for better alignment and visibility */
    &:nth-child(1) { width: 120px; min-width: 120px; } /* Code */
    &:nth-child(2) { width: 120px; min-width: 120px; } /* SKU Image */
    &:nth-child(3) { width: 300px; min-width: 300px; } /* Description */
    &:nth-child(4) { width: 180px; min-width: 180px; } /* Style Code */
    &:nth-child(5) { width: 160px; min-width: 160px; } /* RRP */
    &:nth-child(6) { width: 180px; min-width: 180px; } /* Supplier Specials */
    &:nth-child(7) { width: 220px; min-width: 220px; } /* BWA Buy Price */
    &:nth-child(8) { width: 180px; min-width: 180px; } /* Regional Retail */
    &:nth-child(9) { width: 180px; min-width: 180px; } /* Regional Trade */
    &:nth-child(10) { width: 180px; min-width: 180px; } /* Metro Retail */
    &:nth-child(11) { width: 180px; min-width: 180px; } /* Metro Trade */
    &:nth-child(12) { width: 200px; min-width: 200px; } /* Regional Retail VIP */
    &:nth-child(13) { width: 220px; min-width: 220px; } /* Regional Trade VIP 1 */
    &:nth-child(14) { width: 220px; min-width: 220px; } /* Regional Trade VIP2 */
    &:nth-child(15) { width: 200px; min-width: 200px; } /* Metro Retail VIP */
    &:nth-child(16) { width: 220px; min-width: 220px; } /* Metro Trade VIP1 */
    &:nth-child(17) { width: 220px; min-width: 220px; } /* Metro Trade VIP2 */
    &:nth-child(18) { width: 400px; min-width: 400px; } /* Supplier Specials Status */
    &:nth-child(19) { width: 500px; min-width: 500px; } /* Supplier Specials Start Date */
    &:nth-child(20) { width: 500px; min-width: 500px; } /* Supplier Specials End Date */
    &:nth-child(21) { width: 150px; min-width: 150px; } /* Main Status Column */
    
    /* Specific class targeting for Status column - multiple selectors for safety */
    &.status-header-column {
      width: 150px !important;
      min-width: 150px !important;
      max-width: 150px !important;
    }
    
    /* Notes column - always last, use class and last-child for maximum coverage */
    &.notes-header-column,
    &:last-child.notes-header-column {
      width: 60px !important;
      min-width: 60px !important;
      max-width: 60px !important;
      padding: 8px 4px !important;
      border-right: none !important;
    }
    
    /* Backup selector for Notes column when it's the last child */
    tr:first-child > &:last-child:not(.status-header-column) {
      width: 60px !important;
      min-width: 60px !important;
      max-width: 60px !important;
      padding: 8px 4px !important;
    }
    
    &:nth-child(21) {
      border-right: 1px solid rgba(255, 255, 255, 0.2) !important;
    }
    
    /* Extra backup selectors based on user's console finding */
    &:nth-child(19) {
      &.status-header-column {
        width: 150px !important;
        min-width: 150px !important;
        max-width: 150px !important;
      }
    }
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid #E5E7EB;
    
    &:nth-child(even) {
      background-color: #F9FAFB;
    }
    
    &:last-child {
      border-bottom: none;
    }
    
    &:hover {
      background-color: #F3F4F6;
    }

    /* Supplier Special active rows */
    &.supplier-special-active {
      background-color: #fff3cd !important; /* Light yellow background */
      border-left: 4px solid #ffc107; /* Yellow left border */
    }

    &.supplier-special-active:hover {
      background-color: #fff3b8 !important; /* Slightly darker yellow on hover */
    }
  }
  
  td {
    padding: 10px 8px;
    font-size: 13px;
    border-right: 1px solid #E5E7EB;
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    vertical-align: middle;
    line-height: 1.3;
    
    &:last-child:not(.notes-cell-column) {
      border-right: none;
    }
    
    &.number-cell {
      text-align: right;
      font-family: 'Courier New', monospace;
      font-weight: 500;
    }
    
    &.status-cell {
      text-align: center;
    }
    
    /* Main Status column cells - multiple selectors for maximum coverage */
    &.status-cell-column {
      width: 150px !important;
      min-width: 150px !important;
      max-width: 150px !important;
      text-align: center;
    }
    
    /* Notes column cells - use class and last-child for maximum coverage */
    &.notes-cell-column,
    &:last-child.notes-cell-column {
      width: 60px !important;
      min-width: 60px !important;
      max-width: 60px !important;
      padding: 8px 4px !important;
      text-align: center;
      border-right: none !important;
    }
    
    /* Backup selector for Notes column when it's the last child */
    &:last-child:not(.status-cell-column) {
      width: 60px !important;
      min-width: 60px !important;
      max-width: 60px !important;
      padding: 8px 4px !important;
      text-align: center;
      border-right: none !important;
    }
    
    &:nth-child(21) {
      border-right: 1px solid #E5E7EB !important;
    }
    
    /* Description column - left aligned and allows text wrapping */
    &:nth-child(3) {
      text-align: left;
      white-space: normal;
      line-height: 1.4;
      word-wrap: break-word;
    }
    
    /* Code column */
    &:nth-child(1) {
      font-weight: 600;
      color: #042B41;
    }
    
    /* SKU Image column */
    &:nth-child(2) {
      padding: 8px;
      text-align: center;
      vertical-align: middle;
    }
    
    /* Style code column */
    &:nth-child(4) {
      font-family: 'Courier New', monospace;
      font-size: 12px;
    }
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: white;
  z-index: 10;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  border-top: 1px solid #E5E7EB;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 0.75rem;
    padding: 1rem;
  }
`;

const CancelButton = styled.button`
  padding: 0.75rem 2rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  background-color: white;
  color: #111827;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  min-width: 120px;
  height: 44px;
  
  &:hover {
    background-color: #F9FAFB;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SaveButton = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 0.375rem;
  background-color: #042B41;
  color: white;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  min-width: 120px;
  height: 44px;
  
  &:hover:not(:disabled) {
    background-color: #031F30;
  }
  
  &:disabled {
    background-color: #9CA3AF;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 3rem 2rem;
  
  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }
`;

// Modal components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 2rem;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    max-height: 95vh;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin: 0.5rem;
  }
`;

const SubmittingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.75);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
`;

const SubmittingText = styled.p`
  margin-top: 1rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.95rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin: 0 0 1rem 0;
  text-align: center;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const ModalSubtitle = styled.p`
  font-size: 1rem;
  color: #333;
  margin: 0 0 1.5rem 0;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin: 0 0 1rem 0;
  }
`;

const TemplateDownloadContainer = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const TemplateLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #2563EB;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  border: 1px solid #2563EB;
  border-radius: 0.375rem;
  transition: all 0.2s;
  
  &:hover {
    background-color: #EBF5FF;
    text-decoration: none;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed #D1D5DB;
  border-radius: 8px;
  padding: 2.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #F9FAFB;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #042B41;
    background-color: #F3F4F6;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const UploadIcon = styled.div`
  margin-bottom: 1rem;
  
  img {
    width: 48px;
    height: 48px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
    
    img {
      width: 36px;
      height: 36px;
    }
  }
`;

const FileText = styled.p`
  font-size: 1rem;
  color: #333;
  margin: 0;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const CancelModalButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  background-color: white;
  color: #111827;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  height: 44px;
  
  &:hover {
    background-color: #F9FAFB;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    font-size: 0.8rem;
    height: 40px;
  }
`;

const SubmitModalButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 0.375rem;
  background-color: #042B41;
  color: white;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  height: 44px;
  
  &:hover {
    background-color: #031F30;
  }
  
  &:disabled {
    background-color: #9CA3AF;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    font-size: 0.8rem;
    height: 40px;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

// Loading spinner component
const LoadingSpinner = styled.div`
  display: inline-block;
  width: 2rem;
  height: 2rem;
  border: 0.25rem solid rgba(4, 43, 65, 0.3);
  border-radius: 50%;
  border-top-color: #042B41;
  animation: spin 1s ease-in-out infinite;
  margin: 2rem auto;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Add a component for displaying error messages
const ErrorMessage = styled.div`
  color: #EF4444;
  background-color: #FEE2E2;
  border: 1px solid #FCA5A5;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #EF4444;
  font-size: 1.25rem;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  margin-left: 1rem;
  line-height: 1;
  
  &:hover {
    opacity: 0.7;
  }
`;

// Add a component for success messages
const SuccessMessage = styled.div`
  color: #047857;
  background-color: #D1FAE5;
  border: 1px solid #A7F3D0;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const SuccessCloseButton = styled.button`
  background: none;
  border: none;
  color: #047857;
  font-size: 1.25rem;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  margin-left: 1rem;
  line-height: 1;
  
  &:hover {
    opacity: 0.7;
  }
`;

// Create extra padding at the bottom to prevent content from being hidden behind the fixed buttons
const BottomSpacer = styled.div`
  height: 60px;
  width: 100%;
`;

// Update content wrapper to handle proper positioning
const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 1rem;
`;

// Simplified styled components for Supplier Specials
const SpecialsDateInput = styled.input<{ disabled?: boolean }>`
  padding: 6px 8px;
  border: 1px solid ${props => props.disabled ? '#E5E7EB' : '#D1D5DB'};
  border-radius: 4px;
  font-size: 12px;
  width: 100%;
  background-color: ${props => props.disabled ? '#F9FAFB' : 'white'};
  color: ${props => props.disabled ? '#9CA3AF' : '#111827'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.disabled ? '#E5E7EB' : '#042B41'};
    box-shadow: ${props => props.disabled ? 'none' : '0 0 0 2px rgba(4, 43, 65, 0.1)'};
  }
  
  /* Custom placeholder styling for better visibility */
  &::placeholder {
    color: #9CA3AF;
    opacity: 1;
    font-size: 11px;
    text-transform: uppercase;
  }
  
  &::-webkit-input-placeholder {
    color: #9CA3AF;
    opacity: 1;
    font-size: 11px;
    text-transform: uppercase;
  }
  
  &::-moz-placeholder {
    color: #9CA3AF;
    opacity: 1;
    font-size: 11px;
    text-transform: uppercase;
  }
  
  /* Force date input placeholder to show as uppercase */
  &[type="date"]::-webkit-datetime-edit-text {
    color: #9CA3AF;
    text-transform: uppercase;
  }
  
  &[type="date"]::-webkit-datetime-edit-month-field {
    color: #9CA3AF;
    text-transform: uppercase;
  }
  
  &[type="date"]::-webkit-datetime-edit-day-field {
    color: #9CA3AF;
    text-transform: uppercase;
  }
  
  &[type="date"]::-webkit-datetime-edit-year-field {
    color: #9CA3AF;
    text-transform: uppercase;
  }
  
  /* Show placeholder when no value */
  &:invalid {
    color: #9CA3AF;
  }
`;

// Update the interface for product items to match our API
interface Product extends PriceItem {
  // Additional properties or UI-specific properties can be added here
  supplier_specials_price?: number; // Add field for supplier specials pricing
  supplier_specials_status?: boolean; // Add field for supplier specials status toggle
  supplier_specials_start_date?: string; // Add field for supplier specials start date
  supplier_specials_end_date?: string; // Add field for supplier specials end date
  notes?: string; // Add field for notes
}

// Helper function to get pricing values from PriceList object
// Helper function to get pricing values from PriceList object
const getPriceValue = (product: Product, priceListId: string): string => {
  const price = product.PriceList?.[priceListId] || 0;
  return `$${price.toFixed(2)}`;
};

// Helper function to format RRP price
const formatRRPPrice = (price: number | undefined): string => {
  return `$${(price || 0).toFixed(2)}`;
};

// Helper function to format BWA Buy Price
const formatBWABuyPrice = (price: number | undefined): string => {
  return `$${(price || 0).toFixed(2)}`;
};

interface PriceInputFieldProps {
  value: string;
  onChange: (val: string) => void;
  style?: React.CSSProperties;
}

const PriceInputField: React.FC<PriceInputFieldProps> = ({ value, onChange, style }) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value);
    }
  }, [value, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setLocalValue(newVal);
    onChange(newVal);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setLocalValue(value.replace('$', ''));
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <FormInput
      type="text"
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={style}
    />
  );
};



// Add helper function to get row styling for supplier special pricing
const getRowClassName = (product: Product): string => {
  return shouldApplySupplierSpecial(product) ? 'supplier-special-active' : '';
};

// Price List ID mappings with YOUR naming convention (Pascal Case) + CORRECTED database mapping
const PRICE_LIST_IDS = {
  RegionalRetail: '1',        // ID 1: Regional Retail AUD Incl
  RegionalTrade: '2',         // ID 2: Regional Trade AUD Incl  
  MetroRetail: '3',           // ID 3: Metro Retail AUD Incl
  MetroTrade: '4',            // ID 4: Metro Trade AUD Incl
  RegionalRetailVIP: '5',     // ID 5: Regional Retail VIP AUD Incl
  RegionalTradeVIP1: '6',     // ID 6: Regional Trade VIP1 AUD Incl
  RegionalTradeVIP2: '7',     // ID 7: Regional Trade VIP2 AUD Incl
  MetroRetailVIP: '8',        // ID 8: Metro Retail VIP AUD Incl
  MetroTradeVIP1: '9',        // ID 9: Metro Trade VIP1 AUD Incl
  MetroTradeVIP2: '10',       // ID 10: Metro Trade VIP2 AUD Incl
} as const;

// Add Toggle Switch Component
const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
`;

const ToggleSlider = styled.span<{ checked: boolean; disabled?: boolean }>`
  position: absolute;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.disabled ? '#e5e7eb' : (props.checked ? '#042B41' : '#ccc')};
  transition: .4s;
  border-radius: 20px;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:before {
    position: absolute;
    content: "";
    height: 14px;
    width: 14px;
    left: ${props => props.checked ? '19px' : '3px'};
    bottom: 3px;
    background-color: ${props => props.disabled ? '#9ca3af' : 'white'};
    transition: .4s;
    border-radius: 50%;
  }
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
`;

// Add utility function to check if supplier special pricing should be applied
// For UI calculation purposes, we apply supplier special pricing when:
// 1. Status is ON, AND 2. Both dates are set (regardless of current date)
// This allows users to preview future supplier special pricing
const shouldApplySupplierSpecial = (product: Product): boolean => {
  if (!product.supplier_specials_status) {
    return false;
  }
  
  // For UI purposes, we just need both dates to be set
  // The actual date validation for active promotions is handled by the backend
  if (!product.supplier_specials_start_date || !product.supplier_specials_end_date) {
    return false;
  }
  
  // Check that the dates are not empty strings
  if (product.supplier_specials_start_date.trim() === '' || product.supplier_specials_end_date.trim() === '') {
    return false;
  }
  
  return true;
};

// Styled components for search functionality
const SearchContainer = styled.div`
  margin-bottom: 1rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  min-width: 420px;
  
  @media (max-width: 640px) {
    min-width: 100%;
  }
  
  &:focus {
    outline: none;
    border-color: #042B41;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }
`;

const SearchLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const NotesIcon = styled.span<{ hasNotes?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    filter: ${props => props.hasNotes ? 'invert(15%) sepia(95%) saturate(6932%) hue-rotate(358deg) brightness(95%) contrast(112%)' : 'none'};
  }
`;

const NotesModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const NotesModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 20px;
`;

const NotesModalTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin: 0 0 20px 0;
  text-align: center;
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  height: 150px;
  padding: 12px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 20px;
  resize: none;

  &:focus {
    outline: none;
    border-color: #042B41;
  }
`;

const NotesButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
`;

const NotesButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  min-width: 120px;
`;

const SaveNotesButton = styled(NotesButton)`
  background-color: #042B41;
  color: white;
  border: none;

  &:hover {
    background-color: #031F30;
  }
`;

const CloseNotesButton = styled(NotesButton)`
  background-color: white;
  color: #111827;
  border: 1px solid #D1D5DB;

  &:hover {
    background-color: #F9FAFB;
  }
`;



const SupplierDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoadingUser, isAuthenticated } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const location = useLocation();

  // State for supplier data
  const [supplier, setSupplier] = useState<ApiSupplier | null>(null);
  const [supplierName, setSupplierName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [showPriceListModal, setShowPriceListModal] = useState<boolean>(false);
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const [currentNotesProductId, setCurrentNotesProductId] = useState<number | null>(null);
  const [notesValue, setNotesValue] = useState<string>('');
  const [isSavingNotes, setIsSavingNotes] = useState<boolean>(false);
  
  // State for current pricing configuration
  const [currentPricingConfig, setCurrentPricingConfig] = useState<any>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Price List input fields state
  const [priceListData, setPriceListData] = useState({
    regionalRetailMarkup: '',
    regionalRetailVanityMarkup: '',
    regionalRetailSupplierSpecialMarkup: '',
    regionalRetailSupplierSpecialVanityMarkup: '',
    regionalTradeMarkup: '',
    regionalTradeSupplierSpecialMarkup: '',
    metroRetailMarkup: '',
    metroRetailVanityMarkup: '',
    metroRetailSupplierSpecialMarkup: '',
    metroRetailSupplierSpecialVanityMarkup: '',
    metroTradeMarkup: '',
    metroTradeSupplierSpecialMarkup: '',
    regionalRetailVIPMarkup: '',
    regionalRetailVIPVanityMarkup: '',
    regionalRetailVIPSupplierSpecialMarkup: '',
    regionalRetailVIPSupplierSpecialVanityMarkup: '',
    regionalTradeVIP1Markup: '',
    regionalTradeVIP1SupplierSpecialMarkup: '',
    regionalTradeVIP2Markup: '',
    regionalTradeVIP2SupplierSpecialMarkup: '',
    metroRetailVIPMarkup: '',
    metroRetailVIPVanityMarkup: '',
    metroRetailVIPSupplierSpecialMarkup: '',
    metroRetailVIPSupplierSpecialVanityMarkup: '',
    metroTradeVIP1Markup: '',
    metroTradeVIP1SupplierSpecialMarkup: '',
    metroTradeVIP2Markup: '',
    metroTradeVIP2SupplierSpecialMarkup: ''
  });

  // Supplier form data
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  // Original data for change detection
  const [originalData, setOriginalData] = useState({
    supplierName: '',
    email: '',
    phone: '',
    address: '',
    products: [] as Product[]
  });

  // Original price list configuration for change detection
  const [originalPriceListData, setOriginalPriceListData] = useState({
    regionalRetailMarkup: '',
    regionalRetailVanityMarkup: '',
    regionalRetailSupplierSpecialMarkup: '',
    regionalRetailSupplierSpecialVanityMarkup: '',
    regionalTradeMarkup: '',
    regionalTradeSupplierSpecialMarkup: '',
    metroRetailMarkup: '',
    metroRetailVanityMarkup: '',
    metroRetailSupplierSpecialMarkup: '',
    metroRetailSupplierSpecialVanityMarkup: '',
    metroTradeMarkup: '',
    metroTradeSupplierSpecialMarkup: '',
    regionalRetailVIPMarkup: '',
    regionalRetailVIPVanityMarkup: '',
    regionalRetailVIPSupplierSpecialMarkup: '',
    regionalRetailVIPSupplierSpecialVanityMarkup: '',
    regionalTradeVIP1Markup: '',
    regionalTradeVIP1SupplierSpecialMarkup: '',
    regionalTradeVIP2Markup: '',
    regionalTradeVIP2SupplierSpecialMarkup: '',
    metroRetailVIPMarkup: '',
    metroRetailVIPVanityMarkup: '',
    metroRetailVIPSupplierSpecialMarkup: '',
    metroRetailVIPSupplierSpecialVanityMarkup: '',
    metroTradeVIP1Markup: '',
    metroTradeVIP1SupplierSpecialMarkup: '',
    metroTradeVIP2Markup: '',
    metroTradeVIP2SupplierSpecialMarkup: ''
  });

  // Validate supplier specials: if status is ON, both dates must be filled
  const isSupplierSpecialsValid = useMemo(() => {
    return products.every(product => {
      if (product.supplier_specials_status) {
        return product.supplier_specials_start_date && 
               product.supplier_specials_start_date.trim() !== '' &&
               product.supplier_specials_end_date && 
               product.supplier_specials_end_date.trim() !== '';
      }
      return true;
    });
  }, [products]);

  // Check if there are any changes with supplier specials validation
  const hasChanges = useMemo(() => {
    // Check basic form fields
    const hasBasicChanges = (
      supplierName !== originalData.supplierName ||
      email !== originalData.email ||
      phone !== originalData.phone ||
      address !== originalData.address
    );

    // More robust product comparison - only check specific fields that matter
    const hasProductChanges = products.length !== originalData.products.length || 
      products.some((product, index) => {
        const originalProduct = originalData.products[index];
        if (!originalProduct) return true;
        
        // Compare only the fields that users can actually change
        return (
          product.bwa_buy_price_50_ex_gst !== originalProduct.bwa_buy_price_50_ex_gst ||
          product.supplier_specials_price !== originalProduct.supplier_specials_price ||
          product.supplier_specials_status !== originalProduct.supplier_specials_status ||
          product.supplier_specials_start_date !== originalProduct.supplier_specials_start_date ||
          product.supplier_specials_end_date !== originalProduct.supplier_specials_end_date ||
          product.status !== originalProduct.status
        );
      });

    // Basic form changes should always be saveable, regardless of supplier specials validation
    // Product changes should only be saveable if supplier specials are valid
    const result = hasBasicChanges || (hasProductChanges && isSupplierSpecialsValid);
    
    return result;
  }, [supplierName, email, phone, address, products, originalData, isSupplierSpecialsValid]);

  // Check if there are any changes in price list configuration
  const hasPriceListChanges = useMemo(() => {
    return JSON.stringify(priceListData) !== JSON.stringify(originalPriceListData);
  }, [priceListData, originalPriceListData]);

  // Helper function to build suppliers URL with preserved pagination state
  const buildSuppliersURL = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    const page = urlParams.get('page');
    const limit = urlParams.get('limit');
    const search = urlParams.get('search');

    const suppliersParams = new URLSearchParams();
    if (page) suppliersParams.set('page', page);
    if (limit) suppliersParams.set('limit', limit);
    if (search) suppliersParams.set('search', search);

    return suppliersParams.toString()
      ? `/suppliers?${suppliersParams.toString()}`
      : '/suppliers';
  }, [location.search]);

  // State for supplier data


  // Function to fetch pricing configuration
  const fetchPricingConfig = useCallback(async (supplierId: number) => {
    try {
      const pricingConfig = await supplierService.getSupplierPricingConfig(supplierId);
      setCurrentPricingConfig(pricingConfig);
      return pricingConfig;
    } catch (error) {
      console.error('Error fetching pricing configuration:', error);
      return null;
    }
  }, []);

  // Memoize the fetchSupplierDetails function to prevent infinite loops
  const fetchSupplierDetails = useCallback(async (supplierId: number, page: number = 1, limit: number = 10, search: string = '') => {
    setLoading(true);

    try {
      console.log(`Fetching supplier details for ID: ${supplierId}, page: ${page}, limit: ${limit}, search: ${search}`);

      // Calculate skip value for API
      const skip = (page - 1) * limit;

      // Use the new paginated API endpoint
      const supplierData = await supplierService.getSupplierWithPagination(supplierId, skip, limit, search || undefined);
      console.log('Paginated supplier data received:', supplierData);

      if (!supplierData || !supplierData.name) {
        throw new Error('Received incomplete supplier data from API');
      }

        // Update form state with supplier data
      setSupplierName(supplierData.supplier_name || supplierData.name || '');
      setPhone(supplierData.phone_no || supplierData.phone || '');
      setEmail(supplierData.email || '');
      setAddress(supplierData.address || '');

      // Update pagination state
      setTotalItems(supplierData.total || 0);
      setTotalPages(supplierData.pages || 1);
      setCurrentPage(supplierData.page || 1);

        // Handle price list items if they exist
      if (supplierData.price_list && Array.isArray(supplierData.price_list) && supplierData.price_list.length > 0) {
          // Map the response data to ensure we use 'sku' field consistently
        const mappedPriceList = supplierData.price_list.map((item: any) => ({
            ...item,
            sku: item.sku || { id: 0, code: item.code || '' }, // Handle both new and old formats
            supplier_specials_price: item.supplier_specials_price || item.bwa_buy_price_50_ex_gst || 0, // Initialize supplier specials price
            supplier_specials_status: item.supplier_specials_status ?? false, // Initialize supplier specials status (use ?? to handle boolean properly)
            supplier_specials_start_date: item.supplier_specials_start_date || '', // Initialize supplier specials start date
            supplier_specials_end_date: item.supplier_specials_end_date || '', // Initialize supplier specials end date
            notes: item.notes || '', // Initialize notes
            status: item.status !== false, // Initialize status to boolean
          }));
          setProducts(mappedPriceList);

        // Store original data for change detection (only for the current page)
          setOriginalData({
          supplierName: supplierData.supplier_name || supplierData.name || '',
          email: supplierData.email || '',
          phone: supplierData.phone_no || supplierData.phone || '',
          address: supplierData.address || '',
            products: mappedPriceList.map((item: any) => ({
              ...item,
              sku: { ...(item.sku || {}) }
            }))
          });
        } else {
          // Initialize with empty array to prevent undefined errors
          setProducts([]);

          // Store original data for change detection
          setOriginalData({
          supplierName: supplierData.supplier_name || supplierData.name || '',
          email: supplierData.email || '',
          phone: supplierData.phone_no || supplierData.phone || '',
          address: supplierData.address || '',
            products: []
          });
      }
      

    } catch (err) {
      console.error('Error in fetchSupplierDetails:', err);

      // Display a meaningful error to the user
      let errorMessage = 'Failed to load supplier details.';

      if (err instanceof Error) {
        errorMessage = err.message;
      }

      toast.showToast(errorMessage, { type: 'error' });

      // Create minimal supplier data to prevent UI crashes
      const currentId = id ? parseInt(id, 10) : 0;
      setSupplierName(`Supplier ${currentId}`);
      setPhone('');
      setEmail('');
      setAddress('');
      setProducts([]);
      setTotalItems(0);
      setTotalPages(1);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  }, [id, toast.showToast]);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page); // Update current page state
    if (id) {
      const numericId = parseInt(id, 10);
      if (!isNaN(numericId)) {
        fetchSupplierDetails(numericId, page, itemsPerPage, searchQuery);
      }
    }
  }, [id, itemsPerPage, searchQuery, fetchSupplierDetails]);

  const handleItemsPerPageChange = useCallback((newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    if (id) {
      const numericId = parseInt(id, 10);
      if (!isNaN(numericId)) {
        fetchSupplierDetails(numericId, 1, newItemsPerPage, searchQuery);
      }
    }
  }, [id, searchQuery, fetchSupplierDetails]);

  const handleSearchChange = useCallback((newSearchQuery: string) => {
    setSearchQuery(newSearchQuery);
    setCurrentPage(1); // Reset to first page when searching
    if (id) {
      const numericId = parseInt(id, 10);
      if (!isNaN(numericId)) {
        fetchSupplierDetails(numericId, 1, itemsPerPage, newSearchQuery);
      }
    }
  }, [id, itemsPerPage, fetchSupplierDetails]);

  // Fetch supplier data on component mount
  useEffect(() => {
    if (id) {
      // Validate that the ID is a valid number
      const numericId = parseInt(id, 10);

      if (isNaN(numericId)) {
        toast.showToast(`Invalid supplier ID: ${id}. The ID must be a number.`, { type: 'error' });
        setLoading(false);
        return;
      }

      // Fetch both supplier details and pricing configuration
      fetchSupplierDetails(numericId, currentPage, itemsPerPage, searchQuery);
      fetchPricingConfig(numericId);
    } else {
      toast.showToast('No supplier ID provided. Please select a valid supplier.', { type: 'error' });
      setLoading(false);
    }
  }, [id, fetchSupplierDetails, fetchPricingConfig, currentPage, itemsPerPage, searchQuery]); // Added missing dependencies

  // Recalculate prices when both pricing config and products are loaded
  // This ensures supplier specials pricing is applied correctly on page load/refresh
  useEffect(() => {
    if (currentPricingConfig && products.length > 0) {
      // Small delay to ensure all state is properly set
      const timer = setTimeout(() => {
        // Inline price recalculation logic to avoid dependency issues
        const updatedProducts = products.map(product => {
          const basePrice = product.bwa_buy_price_50_ex_gst || 0;
          
          // Check if supplier special pricing should be applied for this product
          const useSpecialPricing = shouldApplySupplierSpecial(product);
          
          // Determine if product is a vanity based on description
          const isVanity = (product.description || '').toLowerCase().includes('vanity');
          
          // Calculate new prices based on supplier special status and dates
          let regionalRetailMarkup, metroRetailMarkup, regionalRetailVIPMarkup, metroRetailVIPMarkup;
          
          if (useSpecialPricing) {
            regionalRetailMarkup = isVanity 
              ? currentPricingConfig.regional_retail_supplier_special_vanity_markup
              : currentPricingConfig.regional_retail_supplier_special_markup;
            metroRetailMarkup = isVanity
              ? currentPricingConfig.metro_retail_supplier_special_vanity_markup 
              : currentPricingConfig.metro_retail_supplier_special_markup;
            regionalRetailVIPMarkup = isVanity
              ? currentPricingConfig.regional_retail_vip_supplier_special_vanity_markup
              : currentPricingConfig.regional_retail_vip_supplier_special_markup;
            metroRetailVIPMarkup = isVanity
              ? currentPricingConfig.metro_retail_vip_supplier_special_vanity_markup
              : currentPricingConfig.metro_retail_vip_supplier_special_markup;
          } else {
            regionalRetailMarkup = isVanity 
              ? currentPricingConfig.regional_retail_vanity_markup
              : currentPricingConfig.regional_retail_markup;
            metroRetailMarkup = isVanity
              ? currentPricingConfig.metro_retail_vanity_markup
              : currentPricingConfig.metro_retail_markup;
            regionalRetailVIPMarkup = isVanity
              ? currentPricingConfig.regional_retail_vip_vanity_markup
              : currentPricingConfig.regional_retail_vip_markup;
            metroRetailVIPMarkup = isVanity
              ? currentPricingConfig.metro_retail_vip_vanity_markup
              : currentPricingConfig.metro_retail_vip_markup;
          }
          
          const regionalRetailPrice = basePrice * regionalRetailMarkup * 1.1; // +10% GST
          const metroRetailPrice = basePrice * metroRetailMarkup * 1.1; // +10% GST
          const regionalRetailVIPPrice = basePrice * regionalRetailVIPMarkup * 1.1; // +10% GST
          const metroRetailVIPPrice = basePrice * metroRetailVIPMarkup * 1.1; // +10% GST
          
          // Trade markups
          const regionalTradeMarkup = useSpecialPricing ? currentPricingConfig.regional_trade_supplier_special_markup : currentPricingConfig.regional_trade_markup;
          const metroTradeMarkup = useSpecialPricing ? currentPricingConfig.metro_trade_supplier_special_markup : currentPricingConfig.metro_trade_markup;
          const regionalTradeVIP1Markup = useSpecialPricing ? currentPricingConfig.regional_trade_vip1_supplier_special_markup : currentPricingConfig.regional_trade_vip1_markup;
          const regionalTradeVIP2Markup = useSpecialPricing ? currentPricingConfig.regional_trade_vip2_supplier_special_markup : currentPricingConfig.regional_trade_vip2_markup;
          const metroTradeVIP1Markup = useSpecialPricing ? currentPricingConfig.metro_trade_vip1_supplier_special_markup : currentPricingConfig.metro_trade_vip1_markup;
          const metroTradeVIP2Markup = useSpecialPricing ? currentPricingConfig.metro_trade_vip2_supplier_special_markup : currentPricingConfig.metro_trade_vip2_markup;
          
          const regionalTradePrice = basePrice * regionalTradeMarkup * 1.1; // +10% GST
          const metroTradePrice = basePrice * metroTradeMarkup * 1.1; // +10% GST
          const regionalTradeVIP1Price = basePrice * regionalTradeVIP1Markup * 1.1; // +10% GST
          const regionalTradeVIP2Price = basePrice * regionalTradeVIP2Markup * 1.1; // +10% GST
          const metroTradeVIP1Price = basePrice * metroTradeVIP1Markup * 1.1; // +10% GST
          const metroTradeVIP2Price = basePrice * metroTradeVIP2Markup * 1.1; // +10% GST
          
          return {
            ...product,
            regional_retail_aud_incl: Number(regionalRetailPrice.toFixed(2)),
            metro_retail_aud_incl: Number(metroRetailPrice.toFixed(2)),
            regional_trade_aud_incl: Number(regionalTradePrice.toFixed(2)),
            metro_trade_aud_incl: Number(metroTradePrice.toFixed(2)),
            regional_retail_vip_aud_incl: Number(regionalRetailVIPPrice.toFixed(2)),
            metro_retail_vip_aud_incl: Number(metroRetailVIPPrice.toFixed(2)),
            regional_trade_vip1_aud_incl: Number(regionalTradeVIP1Price.toFixed(2)),
            regional_trade_vip2_aud_incl: Number(regionalTradeVIP2Price.toFixed(2)),
            metro_trade_vip1_aud_incl: Number(metroTradeVIP1Price.toFixed(2)),
            metro_trade_vip2_aud_incl: Number(metroTradeVIP2Price.toFixed(2))
          };
        });
        
        setProducts(updatedProducts);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [currentPricingConfig]);

  const handleSave = async () => {
    setSaving(true);

    try {
      if (!id) {
        toast.showToast('No supplier ID available for update.', { type: 'error' });
        return;
      }

      // Calculate final prices with current pricing configuration before saving
      let finalProducts = products;
      if (currentPricingConfig) {
        finalProducts = products.map(product => recalculatePricesForProduct(product, currentPricingConfig));
      }

      // Prepare supplier data for update with calculated prices
      const supplierData = {
        supplier_name: supplierName,
        phone_no: phone,
        email: email,
        address: address,
        price_list: finalProducts
      };

      console.log('Updating supplier with data:', supplierData);

      // Call the API to update supplier
      await supplierService.updateSupplier(parseInt(id), supplierData);

      toast.showToast('Supplier information updated successfully', { type: 'success' });

      // Show loading state and refresh data from server to disable save button
      setProducts([]);
      setLoading(true);

      // Refetch supplier data to get the latest data from server
      await fetchSupplierDetails(parseInt(id), currentPage, itemsPerPage, searchQuery);

    } catch (err: any) {
      console.error('Error updating supplier:', err);
      
      // Handle specific error types
      let errorMessage = 'Failed to update supplier information. Please try again.';
      
      if (err.message && typeof err.message === 'string') {
        if (err.message.includes('phone number') && err.message.includes('already exists')) {
          errorMessage = 'This phone number is already used by another supplier. Please use a different phone number.';
        } else if (err.message.includes('email') && err.message.includes('already exists')) {
          errorMessage = 'This email address is already used by another supplier. Please use a different email address.';
        } else if (err.message.includes('name') && err.message.includes('already exists')) {
          errorMessage = 'This supplier name is already taken. Please choose a different name.';
        } else {
          errorMessage = err.message;
        }
      }
      
      toast.showToast(errorMessage, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleViewNotes = (productId: number) => {
    const product = products.find(p => p.sku?.id === productId);
    if (product) {
      setCurrentNotesProductId(productId);
      setNotesValue(product.notes || '');
      setShowNotesModal(true);
    }
  };

  const handleSaveNotes = async () => {
    if (currentNotesProductId === null) return;

    setIsSavingNotes(true);
    try {
      const product = products.find(p => p.sku?.id === currentNotesProductId);
      if (!product) {
        toast.showToast('Product not found', { type: 'error' });
        return;
      }

      await supplierService.updatePriceListItemNotes(currentNotesProductId, notesValue);
      
      const updatedProducts = products.map(p => 
        p.sku?.id === currentNotesProductId 
          ? { ...p, notes: notesValue }
          : p
      );
      setProducts(updatedProducts);
      
      toast.showToast('Notes saved successfully', { type: 'success' });
      setShowNotesModal(false);
      setCurrentNotesProductId(null);
      setNotesValue('');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.showToast('Failed to save notes', { type: 'error' });
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleCancel = () => {
    // Extract the pagination parameters from the current URL
    const suppliersUrl = buildSuppliersURL();
    navigate(suppliersUrl);
  };

  const handleBulkUploadClick = () => {
    setShowBulkUploadModal(true);
  };

  const handleCloseModal = () => {
    setShowBulkUploadModal(false);
    setSelectedFile(null);
  };

  const handlePriceListClick = async () => {
    if (!id) return;
    
    try {
      // Fetch current pricing configuration
      const pricingConfig = await supplierService.getSupplierPricingConfig(parseInt(id));
      
      // Convert markup values to percentages for display (e.g., 1.70 -> 70)
      const regionalRetailMarkup = Math.round((pricingConfig.regional_retail_markup - 1) * 100).toString();
      const regionalTradeMarkup = Math.round((pricingConfig.regional_trade_markup - 1) * 100).toString();
      const metroRetailMarkup = Math.round((pricingConfig.metro_retail_markup - 1) * 100).toString();
      const metroTradeMarkup = Math.round((pricingConfig.metro_trade_markup - 1) * 100).toString();
      const regionalRetailVIPMarkup = Math.round((pricingConfig.regional_retail_vip_markup - 1) * 100).toString();
      const regionalTradeVIP1Markup = Math.round((pricingConfig.regional_trade_vip1_markup - 1) * 100).toString();
      const regionalTradeVIP2Markup = Math.round((pricingConfig.regional_trade_vip2_markup - 1) * 100).toString();
      const metroRetailVIPMarkup = Math.round((pricingConfig.metro_retail_vip_markup - 1) * 100).toString();
      const metroTradeVIP1Markup = Math.round((pricingConfig.metro_trade_vip1_markup - 1) * 100).toString();
      const metroTradeVIP2Markup = Math.round((pricingConfig.metro_trade_vip2_markup - 1) * 100).toString();
      
      // Calculate vanity markups as 10% higher than their standard counterparts
      const regionalRetailVanityMarkup = (parseInt(regionalRetailMarkup) + 10).toString();
      const metroRetailVanityMarkup = (parseInt(metroRetailMarkup) + 10).toString();
      const regionalRetailVIPVanityMarkup = (parseInt(regionalRetailVIPMarkup) + 10).toString();
      const metroRetailVIPVanityMarkup = (parseInt(metroRetailVIPMarkup) + 10).toString();

      // Helper function to check if a supplier special value is custom (not matching standard)
      const isCustomValue = (supplierSpecialValue: number, standardValue: number) => {
        // If supplier special value is different from standard, it's custom
        return Math.abs(supplierSpecialValue - standardValue) > 0.01;
      };

      // Set supplier special percentages: use saved custom values if they exist, otherwise match standard percentages
      const regionalRetailSupplierSpecialMarkup = isCustomValue(pricingConfig.regional_retail_supplier_special_markup, pricingConfig.regional_retail_markup) 
        ? Math.round((pricingConfig.regional_retail_supplier_special_markup - 1) * 100).toString()
        : regionalRetailMarkup;
      
      const regionalRetailSupplierSpecialVanityMarkup = isCustomValue(pricingConfig.regional_retail_supplier_special_vanity_markup, pricingConfig.regional_retail_vanity_markup)
        ? Math.round((pricingConfig.regional_retail_supplier_special_vanity_markup - 1) * 100).toString()
        : regionalRetailVanityMarkup;
      
      const regionalTradeSupplierSpecialMarkup = isCustomValue(pricingConfig.regional_trade_supplier_special_markup, pricingConfig.regional_trade_markup)
        ? Math.round((pricingConfig.regional_trade_supplier_special_markup - 1) * 100).toString()
        : regionalTradeMarkup;
      
      const metroRetailSupplierSpecialMarkup = isCustomValue(pricingConfig.metro_retail_supplier_special_markup, pricingConfig.metro_retail_markup)
        ? Math.round((pricingConfig.metro_retail_supplier_special_markup - 1) * 100).toString()
        : metroRetailMarkup;
      
      const metroRetailSupplierSpecialVanityMarkup = isCustomValue(pricingConfig.metro_retail_supplier_special_vanity_markup, pricingConfig.metro_retail_vanity_markup)
        ? Math.round((pricingConfig.metro_retail_supplier_special_vanity_markup - 1) * 100).toString()
        : metroRetailVanityMarkup;
      
      const metroTradeSupplierSpecialMarkup = isCustomValue(pricingConfig.metro_trade_supplier_special_markup, pricingConfig.metro_trade_markup)
        ? Math.round((pricingConfig.metro_trade_supplier_special_markup - 1) * 100).toString()
        : metroTradeMarkup;
      
      const regionalRetailVIPSupplierSpecialMarkup = isCustomValue(pricingConfig.regional_retail_vip_supplier_special_markup, pricingConfig.regional_retail_vip_markup)
        ? Math.round((pricingConfig.regional_retail_vip_supplier_special_markup - 1) * 100).toString()
        : regionalRetailVIPMarkup;
      
      const regionalRetailVIPSupplierSpecialVanityMarkup = isCustomValue(pricingConfig.regional_retail_vip_supplier_special_vanity_markup, pricingConfig.regional_retail_vip_vanity_markup)
        ? Math.round((pricingConfig.regional_retail_vip_supplier_special_vanity_markup - 1) * 100).toString()
        : regionalRetailVIPVanityMarkup;
      
      const regionalTradeVIP1SupplierSpecialMarkup = isCustomValue(pricingConfig.regional_trade_vip1_supplier_special_markup, pricingConfig.regional_trade_vip1_markup)
        ? Math.round((pricingConfig.regional_trade_vip1_supplier_special_markup - 1) * 100).toString()
        : regionalTradeVIP1Markup;
      
      const regionalTradeVIP2SupplierSpecialMarkup = isCustomValue(pricingConfig.regional_trade_vip2_supplier_special_markup, pricingConfig.regional_trade_vip2_markup)
        ? Math.round((pricingConfig.regional_trade_vip2_supplier_special_markup - 1) * 100).toString()
        : regionalTradeVIP2Markup;
      
      const metroRetailVIPSupplierSpecialMarkup = isCustomValue(pricingConfig.metro_retail_vip_supplier_special_markup, pricingConfig.metro_retail_vip_markup)
        ? Math.round((pricingConfig.metro_retail_vip_supplier_special_markup - 1) * 100).toString()
        : metroRetailVIPMarkup;
      
      const metroRetailVIPSupplierSpecialVanityMarkup = isCustomValue(pricingConfig.metro_retail_vip_supplier_special_vanity_markup, pricingConfig.metro_retail_vip_vanity_markup)
        ? Math.round((pricingConfig.metro_retail_vip_supplier_special_vanity_markup - 1) * 100).toString()
        : metroRetailVIPVanityMarkup;
      
      const metroTradeVIP1SupplierSpecialMarkup = isCustomValue(pricingConfig.metro_trade_vip1_supplier_special_markup, pricingConfig.metro_trade_vip1_markup)
        ? Math.round((pricingConfig.metro_trade_vip1_supplier_special_markup - 1) * 100).toString()
        : metroTradeVIP1Markup;
      
      const metroTradeVIP2SupplierSpecialMarkup = isCustomValue(pricingConfig.metro_trade_vip2_supplier_special_markup, pricingConfig.metro_trade_vip2_markup)
        ? Math.round((pricingConfig.metro_trade_vip2_supplier_special_markup - 1) * 100).toString()
        : metroTradeVIP2Markup;

      const priceListDataObject = {
        regionalRetailMarkup,
        regionalRetailVanityMarkup,
        regionalRetailSupplierSpecialMarkup,
        regionalRetailSupplierSpecialVanityMarkup,
        regionalTradeMarkup,
        regionalTradeSupplierSpecialMarkup,
        metroRetailMarkup,
        metroRetailVanityMarkup,
        metroRetailSupplierSpecialMarkup,
        metroRetailSupplierSpecialVanityMarkup,
        metroTradeMarkup,
        metroTradeSupplierSpecialMarkup,
        regionalRetailVIPMarkup,
        regionalRetailVIPVanityMarkup,
        regionalRetailVIPSupplierSpecialMarkup,
        regionalRetailVIPSupplierSpecialVanityMarkup,
        regionalTradeVIP1Markup,
        regionalTradeVIP1SupplierSpecialMarkup,
        regionalTradeVIP2Markup,
        regionalTradeVIP2SupplierSpecialMarkup,
        metroRetailVIPMarkup,
        metroRetailVIPVanityMarkup,
        metroRetailVIPSupplierSpecialMarkup,
        metroRetailVIPSupplierSpecialVanityMarkup,
        metroTradeVIP1Markup,
        metroTradeVIP1SupplierSpecialMarkup,
        metroTradeVIP2Markup,
        metroTradeVIP2SupplierSpecialMarkup,
      };

      setPriceListData(priceListDataObject);
      
      // Store original data for change detection
      setOriginalPriceListData(priceListDataObject);
      
      setShowPriceListModal(true);
    } catch (error) {
      console.error('Error fetching pricing configuration:', error);
      toast.showToast('Failed to load pricing configuration', { type: 'error' });
    }
  };

  const handleClosePriceListModal = () => {
    setShowPriceListModal(false);
  };

  const handlePriceListInputChange = (field: string, value: string) => {
    // Update the specified field
    const updatedData = {
      ...priceListData,
      [field]: value
    };

    // If a standard markup is changed, update the corresponding vanity markup to be 10% higher
    if (field === 'regionalRetailMarkup') {
      updatedData.regionalRetailVanityMarkup = (parseInt(value) + 10).toString();
    } else if (field === 'metroRetailMarkup') {
      updatedData.metroRetailVanityMarkup = (parseInt(value) + 10).toString();
    } else if (field === 'regionalRetailVIPMarkup') {
      updatedData.regionalRetailVIPVanityMarkup = (parseInt(value) + 10).toString();
    } else if (field === 'metroRetailVIPMarkup') {
      updatedData.metroRetailVIPVanityMarkup = (parseInt(value) + 10).toString();
    }

    // If a standard markup is changed, update the corresponding supplier special markup to match
    if (field === 'regionalRetailMarkup') {
      updatedData.regionalRetailSupplierSpecialMarkup = value;
    } else if (field === 'regionalTradeMarkup') {
      updatedData.regionalTradeSupplierSpecialMarkup = value;
    } else if (field === 'metroRetailMarkup') {
      updatedData.metroRetailSupplierSpecialMarkup = value;
    } else if (field === 'metroTradeMarkup') {
      updatedData.metroTradeSupplierSpecialMarkup = value;
    } else if (field === 'regionalRetailVIPMarkup') {
      updatedData.regionalRetailVIPSupplierSpecialMarkup = value;
    } else if (field === 'regionalTradeVIP1Markup') {
      updatedData.regionalTradeVIP1SupplierSpecialMarkup = value;
    } else if (field === 'regionalTradeVIP2Markup') {
      updatedData.regionalTradeVIP2SupplierSpecialMarkup = value;
    } else if (field === 'metroRetailVIPMarkup') {
      updatedData.metroRetailVIPSupplierSpecialMarkup = value;
    } else if (field === 'metroTradeVIP1Markup') {
      updatedData.metroTradeVIP1SupplierSpecialMarkup = value;
    } else if (field === 'metroTradeVIP2Markup') {
      updatedData.metroTradeVIP2SupplierSpecialMarkup = value;
    }

    // If a vanity markup is changed, update the corresponding supplier special vanity markup to match
    if (field === 'regionalRetailVanityMarkup') {
      updatedData.regionalRetailSupplierSpecialVanityMarkup = value;
    } else if (field === 'metroRetailVanityMarkup') {
      updatedData.metroRetailSupplierSpecialVanityMarkup = value;
    } else if (field === 'regionalRetailVIPVanityMarkup') {
      updatedData.regionalRetailVIPSupplierSpecialVanityMarkup = value;
    } else if (field === 'metroRetailVIPVanityMarkup') {
      updatedData.metroRetailVIPSupplierSpecialVanityMarkup = value;
    }

    setPriceListData(updatedData);
  };

  const handlePriceListSubmit = async () => {
    if (!id) return;
    
    try {
      // Convert percentage values back to markup multipliers (e.g., 70 -> 1.70)
      const configData = {
        // Standard markups
        regional_retail_markup: 1 + (parseFloat(priceListData.regionalRetailMarkup) || 0) / 100,
        regional_retail_vanity_markup: 1 + (parseFloat(priceListData.regionalRetailVanityMarkup) || 0) / 100,
        regional_trade_markup: 1 + (parseFloat(priceListData.regionalTradeMarkup) || 0) / 100,
        metro_retail_markup: 1 + (parseFloat(priceListData.metroRetailMarkup) || 0) / 100,
        metro_retail_vanity_markup: 1 + (parseFloat(priceListData.metroRetailVanityMarkup) || 0) / 100,
        metro_trade_markup: 1 + (parseFloat(priceListData.metroTradeMarkup) || 0) / 100,
        regional_retail_vip_markup: 1 + (parseFloat(priceListData.regionalRetailVIPMarkup) || 0) / 100,
        regional_retail_vip_vanity_markup: 1 + (parseFloat(priceListData.regionalRetailVIPVanityMarkup) || 0) / 100,
        regional_trade_vip1_markup: 1 + (parseFloat(priceListData.regionalTradeVIP1Markup) || 0) / 100,
        regional_trade_vip2_markup: 1 + (parseFloat(priceListData.regionalTradeVIP2Markup) || 0) / 100,
        metro_retail_vip_markup: 1 + (parseFloat(priceListData.metroRetailVIPMarkup) || 0) / 100,
        metro_retail_vip_vanity_markup: 1 + (parseFloat(priceListData.metroRetailVIPVanityMarkup) || 0) / 100,
        metro_trade_vip1_markup: 1 + (parseFloat(priceListData.metroTradeVIP1Markup) || 0) / 100,
        metro_trade_vip2_markup: 1 + (parseFloat(priceListData.metroTradeVIP2Markup) || 0) / 100,
        
        // Supplier Special markups
        regional_retail_supplier_special_markup: 1 + (parseFloat(priceListData.regionalRetailSupplierSpecialMarkup) || 0) / 100,
        regional_retail_supplier_special_vanity_markup: 1 + (parseFloat(priceListData.regionalRetailSupplierSpecialVanityMarkup) || 0) / 100,
        regional_trade_supplier_special_markup: 1 + (parseFloat(priceListData.regionalTradeSupplierSpecialMarkup) || 0) / 100,
        metro_retail_supplier_special_markup: 1 + (parseFloat(priceListData.metroRetailSupplierSpecialMarkup) || 0) / 100,
        metro_retail_supplier_special_vanity_markup: 1 + (parseFloat(priceListData.metroRetailSupplierSpecialVanityMarkup) || 0) / 100,
        metro_trade_supplier_special_markup: 1 + (parseFloat(priceListData.metroTradeSupplierSpecialMarkup) || 0) / 100,
        regional_retail_vip_supplier_special_markup: 1 + (parseFloat(priceListData.regionalRetailVIPSupplierSpecialMarkup) || 0) / 100,
        regional_retail_vip_supplier_special_vanity_markup: 1 + (parseFloat(priceListData.regionalRetailVIPSupplierSpecialVanityMarkup) || 0) / 100,
        regional_trade_vip1_supplier_special_markup: 1 + (parseFloat(priceListData.regionalTradeVIP1SupplierSpecialMarkup) || 0) / 100,
        regional_trade_vip2_supplier_special_markup: 1 + (parseFloat(priceListData.regionalTradeVIP2SupplierSpecialMarkup) || 0) / 100,
        metro_retail_vip_supplier_special_markup: 1 + (parseFloat(priceListData.metroRetailVIPSupplierSpecialMarkup) || 0) / 100,
        metro_retail_vip_supplier_special_vanity_markup: 1 + (parseFloat(priceListData.metroRetailVIPSupplierSpecialVanityMarkup) || 0) / 100,
        metro_trade_vip1_supplier_special_markup: 1 + (parseFloat(priceListData.metroTradeVIP1SupplierSpecialMarkup) || 0) / 100,
        metro_trade_vip2_supplier_special_markup: 1 + (parseFloat(priceListData.metroTradeVIP2SupplierSpecialMarkup) || 0) / 100,
      };
      
      // Update pricing configuration
      await supplierService.updateSupplierPricingConfig(parseInt(id), configData);
      
      // Update the current pricing config state for real-time recalculation
      setCurrentPricingConfig(configData);
      
      // Immediately recalculate prices on frontend for instant visual feedback
      recalculatePricesWithNewConfig(configData);
      
      toast.showToast('Pricing configuration updated successfully! All prices have been recalculated.', { type: 'success' });
      
      setShowPriceListModal(false);
    } catch (error) {
      console.error('Error updating pricing configuration:', error);
      toast.showToast('Failed to update pricing configuration', { type: 'error' });
    }
  };

  const handleFileAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmitFile = async () => {
    if (!selectedFile) {
      toast.showToast('Please select a file to upload.', { type: 'error' });
      return;
    }

    if (!id) {
      toast.showToast('No supplier ID available for upload.', { type: 'error' });
      return;
    }

    try {
      setUploading(true);

      // Validate file type
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
      if (!['xlsx', 'xls'].includes(fileExtension || '')) {
        toast.showToast('Please select an Excel file (.xlsx or .xls).', { type: 'error' });
        return;
      }

      // Call the backend API to upload the price list
      const result = await supplierService.uploadPriceList(parseInt(id), selectedFile);

      // Show success message
      if (result.success > 0) {
        let message = `Successfully uploaded ${result.success} items`;
        if (result.errors > 0) {
          message += ` with ${result.errors} errors`;
        }
        toast.showToast(message, { type: 'success' });

        // Refresh supplier data to show updated price list
        await fetchSupplierDetails(parseInt(id));
      } else {
        toast.showToast('No items were uploaded. Please check your CSV file.', { type: 'warning' });
      }

      // Close modal and reset
      setShowBulkUploadModal(false);
      setSelectedFile(null);

    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload file';
      toast.showToast(`Upload failed: ${errorMessage}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      await generatePriceTemplate();
      toast.showToast('Template downloaded successfully! Please fill it with your data and upload.', { type: 'success' });
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.showToast('Failed to generate template. Please try again.', { type: 'error' });
    }
  };

  const handleBuyPriceChange = (index: number, newPrice: string) => {
    // Remove dollar sign and parse the value
    const numericValue = parseFloat(newPrice.replace('$', '')) || 0;
    const updatedProducts = [...products];
    const currentProduct = updatedProducts[index];
    
    // Update BWA buy price
    updatedProducts[index] = { 
      ...currentProduct, 
      bwa_buy_price_50_ex_gst: numericValue,
      // Sync supplier_specials_price to match BWA price (one-way sync)
      supplier_specials_price: numericValue
    };
    setProducts(updatedProducts);
  };

  const handleSupplierSpecialsPriceChange = (index: number, newPrice: string) => {
    // Remove dollar sign and parse the value
    const numericValue = parseFloat(newPrice.replace('$', '')) || 0;
    const updatedProducts = [...products];
    
    // Update only supplier_specials_price, DO NOT affect BWA buy price
    updatedProducts[index] = { 
      ...updatedProducts[index], 
      supplier_specials_price: numericValue 
    };
    setProducts(updatedProducts);
  };

  const handleSupplierSpecialsStatusChange = (index: number, status: boolean) => {
    const updatedProducts = [...products];
    updatedProducts[index] = { 
      ...updatedProducts[index], 
      supplier_specials_status: status,
      // Clear dates when status is turned off
      supplier_specials_start_date: status ? updatedProducts[index].supplier_specials_start_date : '',
      supplier_specials_end_date: status ? updatedProducts[index].supplier_specials_end_date : ''
    };
    
    setProducts(updatedProducts);
  };

  const handleSupplierSpecialsStartDateChange = (index: number, date: string) => {
    const updatedProducts = [...products];
    updatedProducts[index] = { ...updatedProducts[index], supplier_specials_start_date: date };
    setProducts(updatedProducts);
  };

  const handleSupplierSpecialsEndDateChange = (index: number, date: string) => {
    const updatedProducts = [...products];
    updatedProducts[index] = { ...updatedProducts[index], supplier_specials_end_date: date };
    setProducts(updatedProducts);
  };

  // Helper function to format supplier specials price for display/editing
  const formatSupplierSpecialsPrice = (product: Product): string => {
    // Use supplier_specials_price if it exists, otherwise use BWA buy price
    const specialsPrice = product.supplier_specials_price;
    const bwaPrice = product.bwa_buy_price_50_ex_gst || 0;
    
    // Always use supplier_specials_price if available, otherwise BWA price
    const priceToUse = specialsPrice !== undefined ? specialsPrice : bwaPrice;
    return `$${priceToUse.toFixed(2)}`;
  };

  // Helper function to show the BWA Buy Price in the Supplier Specials column (for non-editable display)
  const calculateSupplierSpecialsPrice = (product: Product): string => {
    // The Supplier Specials column should always show the BWA Buy Price
    // This is a reference column that never changes, regardless of supplier specials status
    const price = product.bwa_buy_price_50_ex_gst || 0;
    return `$${price.toFixed(2)}`;
  };

  // Dynamic price calculation function that considers supplier specials
  const getDynamicPriceValue = (product: Product, priceListId: string): string => {
    // If no pricing config available, use stored price
    if (!currentPricingConfig || !product.bwa_buy_price_50_ex_gst) {
      const price = product.PriceList?.[priceListId] || 0;
      return `$${price.toFixed(2)}`;
    }

    // Calculate price dynamically based on supplier specials status
    // Use supplier_specials_price if it's been explicitly set and is different from BWA price
    // Otherwise use BWA buy price as the base for calculating other prices
    const bwaPrice = product.bwa_buy_price_50_ex_gst;
    const specialsPrice = product.supplier_specials_price;
    
    // Use supplier_specials_price as base if it exists and is different from BWA price
    // This allows Supplier Specials column edits to affect all other price calculations
    const basePrice = (specialsPrice !== undefined && specialsPrice !== bwaPrice) ? specialsPrice : bwaPrice;
    const useSpecialPricing = shouldApplySupplierSpecial(product);
    const isVanity = (product.description || '').toLowerCase().includes('vanity');
    
    let markup = 1.0;
    
    // Map price list IDs to markup fields
    switch (priceListId) {
      case '1': // Regional Retail
        markup = useSpecialPricing 
          ? (isVanity ? currentPricingConfig.regional_retail_supplier_special_vanity_markup : currentPricingConfig.regional_retail_supplier_special_markup)
          : (isVanity ? currentPricingConfig.regional_retail_vanity_markup : currentPricingConfig.regional_retail_markup);
        break;
      case '2': // Regional Trade
        markup = useSpecialPricing 
          ? currentPricingConfig.regional_trade_supplier_special_markup
          : currentPricingConfig.regional_trade_markup;
        break;
      case '3': // Metro Retail
        markup = useSpecialPricing 
          ? (isVanity ? currentPricingConfig.metro_retail_supplier_special_vanity_markup : currentPricingConfig.metro_retail_supplier_special_markup)
          : (isVanity ? currentPricingConfig.metro_retail_vanity_markup : currentPricingConfig.metro_retail_markup);
        break;
      case '4': // Metro Trade
        markup = useSpecialPricing 
          ? currentPricingConfig.metro_trade_supplier_special_markup
          : currentPricingConfig.metro_trade_markup;
        break;
      case '5': // Regional Retail VIP
        markup = useSpecialPricing 
          ? (isVanity ? currentPricingConfig.regional_retail_vip_supplier_special_vanity_markup : currentPricingConfig.regional_retail_vip_supplier_special_markup)
          : (isVanity ? currentPricingConfig.regional_retail_vip_vanity_markup : currentPricingConfig.regional_retail_vip_markup);
        break;
      case '6': // Regional Trade VIP1
        markup = useSpecialPricing 
          ? currentPricingConfig.regional_trade_vip1_supplier_special_markup
          : currentPricingConfig.regional_trade_vip1_markup;
        break;
      case '7': // Regional Trade VIP2
        markup = useSpecialPricing 
          ? currentPricingConfig.regional_trade_vip2_supplier_special_markup
          : currentPricingConfig.regional_trade_vip2_markup;
        break;
      case '8': // Metro Retail VIP
        markup = useSpecialPricing 
          ? (isVanity ? currentPricingConfig.metro_retail_vip_supplier_special_vanity_markup : currentPricingConfig.metro_retail_vip_supplier_special_markup)
          : (isVanity ? currentPricingConfig.metro_retail_vip_vanity_markup : currentPricingConfig.metro_retail_vip_markup);
        break;
      case '9': // Metro Trade VIP1
        markup = useSpecialPricing 
          ? currentPricingConfig.metro_trade_vip1_supplier_special_markup
          : currentPricingConfig.metro_trade_vip1_markup;
        break;
      case '10': // Metro Trade VIP2
        markup = useSpecialPricing 
          ? currentPricingConfig.metro_trade_vip2_supplier_special_markup
          : currentPricingConfig.metro_trade_vip2_markup;
        break;
      default:
        // Fallback to stored price
        const price = product.PriceList?.[priceListId] || 0;
        return `$${price.toFixed(2)}`;
    }
    
    const calculatedPrice = basePrice * markup;
    return `$${calculatedPrice.toFixed(2)}`;
  };

  // Helper function to recalculate prices for a single product
  const recalculatePricesForProduct = (product: Product, config: any): Product => {
    const basePrice = product.bwa_buy_price_50_ex_gst || 0;
    
    // Check if supplier special pricing should be applied for this product
    const useSpecialPricing = shouldApplySupplierSpecial(product);
    
    // Determine if product is a vanity based on description
    const isVanity = (product.description || '').toLowerCase().includes('vanity');
    
    // Calculate new prices based on supplier special status and dates
    let regionalRetailMarkup, metroRetailMarkup, regionalRetailVIPMarkup, metroRetailVIPMarkup;
    
    if (useSpecialPricing) {
      regionalRetailMarkup = isVanity ? config.regional_retail_supplier_special_vanity_markup : config.regional_retail_supplier_special_markup;
      metroRetailMarkup = isVanity ? config.metro_retail_supplier_special_vanity_markup : config.metro_retail_supplier_special_markup;
      regionalRetailVIPMarkup = isVanity ? config.regional_retail_vip_supplier_special_vanity_markup : config.regional_retail_vip_supplier_special_markup;
      metroRetailVIPMarkup = isVanity ? config.metro_retail_vip_supplier_special_vanity_markup : config.metro_retail_vip_supplier_special_markup;
    } else {
      regionalRetailMarkup = isVanity ? config.regional_retail_vanity_markup : config.regional_retail_markup;
      metroRetailMarkup = isVanity ? config.metro_retail_vanity_markup : config.metro_retail_markup;
      regionalRetailVIPMarkup = isVanity ? config.regional_retail_vip_vanity_markup : config.regional_retail_vip_markup;
      metroRetailVIPMarkup = isVanity ? config.metro_retail_vip_vanity_markup : config.metro_retail_vip_markup;
    }
    
    const newPriceList = {
      '1': parseFloat((basePrice * regionalRetailMarkup).toFixed(2)), // Regional Retail
      '2': parseFloat((basePrice * (useSpecialPricing ? config.regional_trade_supplier_special_markup : config.regional_trade_markup)).toFixed(2)), // Regional Trade
      '3': parseFloat((basePrice * metroRetailMarkup).toFixed(2)), // Metro Retail
      '4': parseFloat((basePrice * (useSpecialPricing ? config.metro_trade_supplier_special_markup : config.metro_trade_markup)).toFixed(2)), // Metro Trade
      '5': parseFloat((basePrice * regionalRetailVIPMarkup).toFixed(2)), // Regional Retail VIP
      '6': parseFloat((basePrice * (useSpecialPricing ? config.regional_trade_vip1_supplier_special_markup : config.regional_trade_vip1_markup)).toFixed(2)), // Regional Trade VIP1
      '7': parseFloat((basePrice * (useSpecialPricing ? config.regional_trade_vip2_supplier_special_markup : config.regional_trade_vip2_markup)).toFixed(2)), // Regional Trade VIP2
      '8': parseFloat((basePrice * metroRetailVIPMarkup).toFixed(2)), // Metro Retail VIP
      '9': parseFloat((basePrice * (useSpecialPricing ? config.metro_trade_vip1_supplier_special_markup : config.metro_trade_vip1_markup)).toFixed(2)), // Metro Trade VIP1
      '10': parseFloat((basePrice * (useSpecialPricing ? config.metro_trade_vip2_supplier_special_markup : config.metro_trade_vip2_markup)).toFixed(2)), // Metro Trade VIP2
    };

    return {
      ...product,
      PriceList: {
        ...product.PriceList,
        ...newPriceList
      }
    };
  };

  // Function to recalculate prices based on current pricing configuration
  const recalculatePricesWithNewConfig = (configData: any) => {
    const updatedProducts = products.map(product => {
      const basePrice = product.bwa_buy_price_50_ex_gst || 0;
      
      // Check if supplier special pricing should be applied for this product
      const useSpecialPricing = shouldApplySupplierSpecial(product);
      
      // Determine if product is a vanity based on description
      const isVanity = (product.description || '').toLowerCase().includes('vanity');
      
      // Calculate new prices based on supplier special status and dates - Remove GST calculation
      
      let regionalRetailMarkup, metroRetailMarkup, regionalRetailVIPMarkup, metroRetailVIPMarkup;
      
      if (useSpecialPricing) {
        regionalRetailMarkup = isVanity ? configData.regional_retail_supplier_special_vanity_markup : configData.regional_retail_supplier_special_markup;
        metroRetailMarkup = isVanity ? configData.metro_retail_supplier_special_vanity_markup : configData.metro_retail_supplier_special_markup;
        regionalRetailVIPMarkup = isVanity ? configData.regional_retail_vip_supplier_special_vanity_markup : configData.regional_retail_vip_supplier_special_markup;
        metroRetailVIPMarkup = isVanity ? configData.metro_retail_vip_supplier_special_vanity_markup : configData.metro_retail_vip_supplier_special_markup;
      } else {
        regionalRetailMarkup = isVanity ? configData.regional_retail_vanity_markup : configData.regional_retail_markup;
        metroRetailMarkup = isVanity ? configData.metro_retail_vanity_markup : configData.metro_retail_markup;
        regionalRetailVIPMarkup = isVanity ? configData.regional_retail_vip_vanity_markup : configData.regional_retail_vip_markup;
        metroRetailVIPMarkup = isVanity ? configData.metro_retail_vip_vanity_markup : configData.metro_retail_vip_markup;
      }
      
      const newPriceList = {
        '1': parseFloat((basePrice * regionalRetailMarkup).toFixed(2)), // Regional Retail
        '2': parseFloat((basePrice * (useSpecialPricing ? configData.regional_trade_supplier_special_markup : configData.regional_trade_markup)).toFixed(2)), // Regional Trade
        '3': parseFloat((basePrice * metroRetailMarkup).toFixed(2)), // Metro Retail
        '4': parseFloat((basePrice * (useSpecialPricing ? configData.metro_trade_supplier_special_markup : configData.metro_trade_markup)).toFixed(2)), // Metro Trade
        '5': parseFloat((basePrice * regionalRetailVIPMarkup).toFixed(2)), // Regional Retail VIP
        '6': parseFloat((basePrice * (useSpecialPricing ? configData.regional_trade_vip1_supplier_special_markup : configData.regional_trade_vip1_markup)).toFixed(2)), // Regional Trade VIP1
        '7': parseFloat((basePrice * (useSpecialPricing ? configData.regional_trade_vip2_supplier_special_markup : configData.regional_trade_vip2_markup)).toFixed(2)), // Regional Trade VIP2
        '8': parseFloat((basePrice * metroRetailVIPMarkup).toFixed(2)), // Metro Retail VIP
        '9': parseFloat((basePrice * (useSpecialPricing ? configData.metro_trade_vip1_supplier_special_markup : configData.metro_trade_vip1_markup)).toFixed(2)), // Metro Trade VIP1
        '10': parseFloat((basePrice * (useSpecialPricing ? configData.metro_trade_vip2_supplier_special_markup : configData.metro_trade_vip2_markup)).toFixed(2)), // Metro Trade VIP2
      };

      return {
        ...product,
        PriceList: {
          ...product.PriceList,
          ...newPriceList
        }
      };
    });

    setProducts(updatedProducts);
  };

  const handleBack = () => {
    // Extract the pagination parameters from the current URL
    const suppliersUrl = buildSuppliersURL();
    navigate(suppliersUrl);
  };

  // Block rendering until: supplier data loaded AND auth user object has propagated.
  // Without the (isAuthenticated && !user) guard, the component renders on the first
  // post-login cycle where user is still null but isLoadingUser is already false,
  // causing every role check (user?.role === 'admin') to silently fail.
  if ((loading && products.length === 0) || isLoadingUser || (isAuthenticated && !user)) {
    return (
      <Layout>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Toast notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => { }}
        />
      )}

      <PageContainer>
        <TopSection>
          <TitleSection>
            <BackButtonComponent onClick={handleBack} compact={true} />
            <BreadcrumbNav>
              <SuppliersText onClick={() => navigate(buildSuppliersURL())}>Suppliers Dashboard</SuppliersText>
              <BreadcrumbSeparator>&gt;</BreadcrumbSeparator>
              <SupplierName>{supplierName}</SupplierName>
            </BreadcrumbNav>
          </TitleSection>

          <ButtonsContainer>
            {user?.role === 'admin' && (
            <PriceListButton onClick={handlePriceListClick}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Price List Configuration
            </PriceListButton>
            )}
            <BulkUploadButton onClick={handleBulkUploadClick}>
              <img src={supplierBulkUploadIcon} alt="Upload" />
              Bulk Upload
            </BulkUploadButton>
          </ButtonsContainer>
        </TopSection>

        <ContentContainer>
          <FormContainer>
            <FormRow>
              <FormField>
                <FormLabel required>Supplier Name</FormLabel>
                <FormInput
                  type="text"
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                />
              </FormField>

              <FormField>
                <FormLabel>Email ID</FormLabel>
                <FormInput
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormField>

              <FormField>
                <FormLabel required>Phone No</FormLabel>
                <FormInput
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </FormField>

              <FormField>
                <FormLabel>Address</FormLabel>
                <FormInput
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </FormField>
            </FormRow>
            

          </FormContainer>

          <ContentWrapper>
            {/* Search Box */}
            <SearchContainer>
              <SearchLabel htmlFor="search">Search Products:</SearchLabel>
              <SearchInput
                id="search"
                type="text"
                placeholder="Search by SKU code, description, or style code..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </SearchContainer>

            <TableContainer>
              <TableScrollWrapper>
                <Table isStaffUser={user?.role === 'staff'}>
                  <TableHeader>
                    <tr>
                      <th rowSpan={2}>SKU CODE</th>
                      <th rowSpan={2}>SKU Image</th>
                      <th rowSpan={2}>Product Description</th>
                      <th rowSpan={2}>Style Code</th>
                      {user?.role !== 'staff' && <th rowSpan={2}>RRP (ex GST)</th>}
                      {user?.role !== 'staff' && <th rowSpan={2}>Supplier Specials</th>}
                      {user?.role !== 'staff' && <th rowSpan={2}>BWA BUY PRICE -50% (ex GST)</th>}
                      {user?.role !== 'staff' && <th rowSpan={2}>Regional Retail AUD (ex GST)</th>}
                      {user?.role !== 'staff' && <th rowSpan={2}>Regional Trade AUD (ex GST)</th>}
                      <th rowSpan={2}>Metro Retail AUD (ex GST)</th>
                      <th rowSpan={2}>Metro Trade AUD (ex GST)</th>
                      <th rowSpan={2}>Regional Retail VIP AUD (ex GST)</th>
                      <th rowSpan={2}>Regional Trade VIP 1 AUD (ex GST)</th>
                      <th rowSpan={2}>Regional Trade VIP2 AUD (ex GST)</th>
                      <th rowSpan={2}>Metro Retail VIP AUD (ex GST)</th>
                      <th rowSpan={2}>Metro Trade VIP1 AUD (ex GST)</th>
                      <th rowSpan={2}>Metro Trade VIP2 AUD (ex GST)</th>
                      {user?.role !== 'staff' && <th colSpan={3}>Supplier Specials</th>}
                      {user?.role !== 'staff' && <th rowSpan={2} className="status-header-column">Status</th>}
                      <th rowSpan={2} className="notes-header-column">Notes</th>
                    </tr>
                    <tr>
                      {user?.role !== 'staff' && <th>Status</th>}
                      {user?.role !== 'staff' && <th>Start Date</th>}
                      {user?.role !== 'staff' && <th>End Date</th>}
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {products.map((product, index) => (
                      <tr key={index} className={getRowClassName(product)}>
                        <td>{product.sku?.code || ''}</td>
                        <td>
                          <SkuImage
                            imagePath={product.image_path}
                            alt="SKU Image"
                            width={60}
                            height={60}
                            enableModal={true}
                            showBlankWhenNoImage={true}
                          />
                        </td>
                        <td>{product.description}</td>
                        <td>{product.style_code || ''}</td>
                        {user?.role !== 'staff' && <td className="number-cell">{formatRRPPrice(product.rrp_ex_gst)}</td>}
                        {user?.role !== 'staff' && (
                          <td className="number-cell">
                            {user?.role === 'admin' ? (
                              <PriceInputField
                                value={formatSupplierSpecialsPrice(product)}
                                onChange={(val) => handleSupplierSpecialsPriceChange(index, val)}
                                style={{ 
                                  width: '200px', 
                                  textAlign: 'right', 
                                  margin: '0 auto', 
                                  padding: '6px 8px', 
                                  fontSize: '13px',
                                  fontFamily: 'Courier New, monospace',
                                  fontWeight: '500',
                                  maxWidth: '95%'
                                }}
                              />
                            ) : (
                              formatSupplierSpecialsPrice(product)
                            )}
                          </td>
                        )}
                        {user?.role !== 'staff' && (
                          <td className="number-cell">
                            {user?.role === 'admin' ? (
                              <PriceInputField
                                value={formatBWABuyPrice(product.bwa_buy_price_50_ex_gst)}
                                onChange={(val) => handleBuyPriceChange(index, val)}
                                style={{ 
                                  width: '200px', 
                                  textAlign: 'right', 
                                  margin: '0 auto', 
                                  padding: '6px 8px', 
                                  fontSize: '13px',
                                  fontFamily: 'Courier New, monospace',
                                  fontWeight: '500',
                                  maxWidth: '95%'
                                }}
                              />
                            ) : (
                              formatBWABuyPrice(product.bwa_buy_price_50_ex_gst)
                            )}
                          </td>
                        )}
                        {user?.role !== 'staff' && <td className="number-cell">{getDynamicPriceValue(product, PRICE_LIST_IDS.RegionalRetail)}</td>}
                        {user?.role !== 'staff' && <td className="number-cell">{getDynamicPriceValue(product, PRICE_LIST_IDS.RegionalTrade)}</td>}
                        <td className="number-cell">{getDynamicPriceValue(product, PRICE_LIST_IDS.MetroRetail)}</td>
                        <td className="number-cell">{getDynamicPriceValue(product, PRICE_LIST_IDS.MetroTrade)}</td>
                        <td className="number-cell">{getDynamicPriceValue(product, PRICE_LIST_IDS.RegionalRetailVIP)}</td>
                        <td className="number-cell">{getDynamicPriceValue(product, PRICE_LIST_IDS.RegionalTradeVIP1)}</td>
                        <td className="number-cell">{getDynamicPriceValue(product, PRICE_LIST_IDS.RegionalTradeVIP2)}</td>
                        <td className="number-cell">{getDynamicPriceValue(product, PRICE_LIST_IDS.MetroRetailVIP)}</td>
                        <td className="number-cell">{getDynamicPriceValue(product, PRICE_LIST_IDS.MetroTradeVIP1)}</td>
                        <td className="number-cell">{getDynamicPriceValue(product, PRICE_LIST_IDS.MetroTradeVIP2)}</td>
                        {user?.role !== 'staff' && (
                          <td className="status-cell">
                            <ToggleSwitch>
                              <ToggleInput
                                type="checkbox"
                                checked={product.supplier_specials_status || false}
                                onChange={user?.role === 'admin' ? () => handleSupplierSpecialsStatusChange(index, !product.supplier_specials_status) : undefined}
                                disabled={user?.role !== 'admin'}
                              />
                              <ToggleSlider 
                                checked={product.supplier_specials_status || false} 
                                disabled={user?.role !== 'admin'}
                              />
                            </ToggleSwitch>
                          </td>
                        )}
                        {user?.role !== 'staff' && (
                          <td>
                            <SpecialsDateInput
                              type="date"
                              value={product.supplier_specials_start_date || ''}
                              onChange={(e) => handleSupplierSpecialsStartDateChange(index, e.target.value)}
                              disabled={!product.supplier_specials_status || user?.role !== 'admin'}
                              placeholder="MM/DD/YYYY"
                            />
                          </td>
                        )}
                        {user?.role !== 'staff' && (
                          <td>
                            <SpecialsDateInput
                              type="date"
                              value={product.supplier_specials_end_date || ''}
                              onChange={(e) => handleSupplierSpecialsEndDateChange(index, e.target.value)}
                              disabled={!product.supplier_specials_status || user?.role !== 'admin'}
                              placeholder="MM/DD/YYYY"
                            />
                          </td>
                        )}
                        {user?.role !== 'staff' && (
                          <td className="status-cell status-cell-column">
                            <ToggleSwitch>
                              <ToggleInput
                                type="checkbox"
                                checked={product.status !== false}
                                onChange={() => {
                                  const updatedProducts = [...products];
                                  const currentStatus = product.status !== false;
                                  updatedProducts[index] = {
                                    ...updatedProducts[index],
                                    status: !currentStatus
                                  };
                                  setProducts(updatedProducts);
                                }}
                              />
                              <ToggleSlider checked={product.status !== false} />
                            </ToggleSwitch>
                          </td>
                        )}
                        <td className="notes-cell-column">
                          <NotesIcon
                            hasNotes={!!product.notes && product.notes.length > 0}
                            onClick={() => handleViewNotes(product.sku?.id || 0)}
                          >
                            <img src={invoicesNotesIcon} alt="Notes" />
                          </NotesIcon>
                        </td>
                      </tr>
                    ))}
                  </TableBody>
                </Table>
              </TableScrollWrapper>
            </TableContainer>

            {/* Pagination Component */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[5, 10, 20, 50]}
            />

          </ContentWrapper>

          <BottomSpacer />
        </ContentContainer>

        <ActionButtonsContainer>
          <CancelButton onClick={handleCancel}>Cancel</CancelButton>
          <SaveButton onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? 'Saving...' : 'Save'}
          </SaveButton>
        </ActionButtonsContainer>

        {showBulkUploadModal && (
          <ModalOverlay>
            <ModalContent>
              {uploading && (
                <SubmittingOverlay>
                  <SharedLoadingSpinner size="lg" />
                  <SubmittingText>Uploading price list...</SubmittingText>
                </SubmittingOverlay>
              )}
              <ModalTitle>Bulk Upload Price List</ModalTitle>
              <ModalSubtitle>
                Download the template below, fill it with your data, and upload the completed CSV file.
              </ModalSubtitle>

              <TemplateDownloadContainer>
                <TemplateLink href="#" onClick={(e) => {
                  e.preventDefault();
                  downloadTemplate();
                }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 2V11M8 11L4.5 7.5M8 11L11.5 7.5M2 13H14" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Download PriceTemplate.xlsx
                </TemplateLink>
              </TemplateDownloadContainer>

              <FileUploadArea onClick={uploading ? undefined : handleFileAreaClick}>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                {selectedFile ? (
                  <FileText>Selected file: {selectedFile.name}</FileText>
                ) : (
                  <FileText>Click to select an Excel file</FileText>
                )}
              </FileUploadArea>

              <ButtonContainer>
                <CancelModalButton onClick={uploading ? undefined : handleCloseModal} disabled={uploading}>
                  Cancel
                </CancelModalButton>
                <SubmitModalButton
                  onClick={handleSubmitFile}
                  disabled={!selectedFile || uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </SubmitModalButton>
              </ButtonContainer>
            </ModalContent>
          </ModalOverlay>
        )}

        {showPriceListModal && (
          <ModalOverlay>
            <ModalContent style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
              <ModalTitle>Price List Configuration</ModalTitle>
              <ModalSubtitle>
                Configure markup percentages for different retail and trade categories. These percentages will be applied to the BWA Buy Price to calculate final prices.
              </ModalSubtitle>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'center', fontWeight: '600', borderBottom: '2px solid #e5e7eb', paddingBottom: '0.5rem' }}>
                  <div>Category</div>
                  <div style={{ textAlign: 'right' }}>Standard %</div>
                  <div style={{ textAlign: 'right' }}>Vanity %</div>
                  <div style={{ textAlign: 'right' }}>Supplier Special Standard %</div>
                  <div style={{ textAlign: 'right' }}>Supplier Special Vanity %</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>Regional Retail</div>
                  <FormInput
                    type="number"
                    value={priceListData.regionalRetailMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalRetailMarkup', e.target.value)}
                    placeholder="70"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.regionalRetailVanityMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalRetailVanityMarkup', e.target.value)}
                    placeholder="80"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.regionalRetailSupplierSpecialMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalRetailSupplierSpecialMarkup', e.target.value)}
                    placeholder="65"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.regionalRetailSupplierSpecialVanityMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalRetailSupplierSpecialVanityMarkup', e.target.value)}
                    placeholder="75"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>Regional Trade</div>
                  <FormInput
                    type="number"
                    value={priceListData.regionalTradeMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalTradeMarkup', e.target.value)}
                    placeholder="60"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                  <FormInput
                    type="number"
                    value={priceListData.regionalTradeSupplierSpecialMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalTradeSupplierSpecialMarkup', e.target.value)}
                    placeholder="55"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>Metro Retail</div>
                  <FormInput
                    type="number"
                    value={priceListData.metroRetailMarkup}
                    onChange={(e) => handlePriceListInputChange('metroRetailMarkup', e.target.value)}
                    placeholder="60"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.metroRetailVanityMarkup}
                    onChange={(e) => handlePriceListInputChange('metroRetailVanityMarkup', e.target.value)}
                    placeholder="70"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.metroRetailSupplierSpecialMarkup}
                    onChange={(e) => handlePriceListInputChange('metroRetailSupplierSpecialMarkup', e.target.value)}
                    placeholder="55"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.metroRetailSupplierSpecialVanityMarkup}
                    onChange={(e) => handlePriceListInputChange('metroRetailSupplierSpecialVanityMarkup', e.target.value)}
                    placeholder="65"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>Metro Trade</div>
                  <FormInput
                    type="number"
                    value={priceListData.metroTradeMarkup}
                    onChange={(e) => handlePriceListInputChange('metroTradeMarkup', e.target.value)}
                    placeholder="50"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                  <FormInput
                    type="number"
                    value={priceListData.metroTradeSupplierSpecialMarkup}
                    onChange={(e) => handlePriceListInputChange('metroTradeSupplierSpecialMarkup', e.target.value)}
                    placeholder="45"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>Regional Retail VIP</div>
                  <FormInput
                    type="number"
                    value={priceListData.regionalRetailVIPMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalRetailVIPMarkup', e.target.value)}
                    placeholder="65"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.regionalRetailVIPVanityMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalRetailVIPVanityMarkup', e.target.value)}
                    placeholder="70"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.regionalRetailVIPSupplierSpecialMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalRetailVIPSupplierSpecialMarkup', e.target.value)}
                    placeholder="60"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.regionalRetailVIPSupplierSpecialVanityMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalRetailVIPSupplierSpecialVanityMarkup', e.target.value)}
                    placeholder="65"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>Regional Trade VIP 1</div>
                  <FormInput
                    type="number"
                    value={priceListData.regionalTradeVIP1Markup}
                    onChange={(e) => handlePriceListInputChange('regionalTradeVIP1Markup', e.target.value)}
                    placeholder="55"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                  <FormInput
                    type="number"
                    value={priceListData.regionalTradeVIP1SupplierSpecialMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalTradeVIP1SupplierSpecialMarkup', e.target.value)}
                    placeholder="50"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>Regional Trade VIP 2</div>
                  <FormInput
                    type="number"
                    value={priceListData.regionalTradeVIP2Markup}
                    onChange={(e) => handlePriceListInputChange('regionalTradeVIP2Markup', e.target.value)}
                    placeholder="52"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                  <FormInput
                    type="number"
                    value={priceListData.regionalTradeVIP2SupplierSpecialMarkup}
                    onChange={(e) => handlePriceListInputChange('regionalTradeVIP2SupplierSpecialMarkup', e.target.value)}
                    placeholder="47"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>Metro Retail VIP</div>
                  <FormInput
                    type="number"
                    value={priceListData.metroRetailVIPMarkup}
                    onChange={(e) => handlePriceListInputChange('metroRetailVIPMarkup', e.target.value)}
                    placeholder="55"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.metroRetailVIPVanityMarkup}
                    onChange={(e) => handlePriceListInputChange('metroRetailVIPVanityMarkup', e.target.value)}
                    placeholder="60"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.metroRetailVIPSupplierSpecialMarkup}
                    onChange={(e) => handlePriceListInputChange('metroRetailVIPSupplierSpecialMarkup', e.target.value)}
                    placeholder="50"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <FormInput
                    type="number"
                    value={priceListData.metroRetailVIPSupplierSpecialVanityMarkup}
                    onChange={(e) => handlePriceListInputChange('metroRetailVIPSupplierSpecialVanityMarkup', e.target.value)}
                    placeholder="55"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>Metro Trade VIP 1</div>
                  <FormInput
                    type="number"
                    value={priceListData.metroTradeVIP1Markup}
                    onChange={(e) => handlePriceListInputChange('metroTradeVIP1Markup', e.target.value)}
                    placeholder="45"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                  <FormInput
                    type="number"
                    value={priceListData.metroTradeVIP1SupplierSpecialMarkup}
                    onChange={(e) => handlePriceListInputChange('metroTradeVIP1SupplierSpecialMarkup', e.target.value)}
                    placeholder="40"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>Metro Trade VIP 2</div>
                  <FormInput
                    type="number"
                    value={priceListData.metroTradeVIP2Markup}
                    onChange={(e) => handlePriceListInputChange('metroTradeVIP2Markup', e.target.value)}
                    placeholder="40"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                  <FormInput
                    type="number"
                    value={priceListData.metroTradeVIP2SupplierSpecialMarkup}
                    onChange={(e) => handlePriceListInputChange('metroTradeVIP2SupplierSpecialMarkup', e.target.value)}
                    placeholder="35"
                    style={{ textAlign: 'right', fontFamily: 'Courier New, monospace', fontWeight: '500' }}
                  />
                  <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>N/A</div>
                </div>
              </div>

              <ButtonContainer>
                <CancelModalButton onClick={handleClosePriceListModal}>
                  Cancel
                </CancelModalButton>
                <SubmitModalButton onClick={handlePriceListSubmit} disabled={!hasPriceListChanges}>
                  Save Configuration
                </SubmitModalButton>
              </ButtonContainer>
            </ModalContent>
          </ModalOverlay>
        )}

        {showNotesModal && (
          <NotesModalOverlay>
            <NotesModalContent>
              <NotesModalTitle>Product Notes</NotesModalTitle>
              <NotesTextarea
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                placeholder="Enter notes for this product"
              />
              <NotesButtonContainer>
                <CloseNotesButton onClick={() => {
                  setShowNotesModal(false);
                  setCurrentNotesProductId(null);
                  setNotesValue('');
                }}>
                  Cancel
                </CloseNotesButton>
                <SaveNotesButton onClick={handleSaveNotes}>
                  {isSavingNotes ? 'Saving' : 'Save Notes'}
                </SaveNotesButton>
              </NotesButtonContainer>
            </NotesModalContent>
          </NotesModalOverlay>
        )}
      </PageContainer>
    </Layout>
  );
};

export default SupplierDetailPage; 